<template>
	<div class="gallery-view">
		<!-- Gallery -->
		<template v-if="showGallery">
			<Gallery v-if="results.length" :results="results" @view="onView" />
			<div v-else class="gallery-view__empty">
				<h2>We haven't added anything to the gallery just yet.</h2>
			</div>
			<ButtonGroup>
				<SpinnerButton
					v-if="!fetchedAll"
					class="btn"
					:loading="loading"
					@click.native="() => getGallery()"
					>See more</SpinnerButton
				>
			</ButtonGroup>
			<div class="gallery-view__buttons">
				<StateButtons :exclude="['gallery']" />
			</div>
		</template>
		<!-- Loading -->
		<div v-else class="gallery-view__loading">
			<Loading color="white" />
		</div>
		<!-- Single view overlay -->
		<portal>
			<transition name="fade">
				<PolaroidView
					v-if="viewing"
					darken
					:submission="viewing"
					@dismiss="dismiss"
				/>
			</transition>
		</portal>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import toast from '@/mixins/toast'
import Loading from '@/components/shared/Loading'
import StateButtons from '@/components/main/StateButtons'
import SpinnerButton from '@/components/main/SpinnerButton'
import Gallery from '@/components/main/Gallery'
import ButtonGroup from '@/components/main/ButtonGroup'
import PolaroidView from '@/components/main/PolaroidView'

export default {
	name: 'GalleryView',
	data() {
		return {
			viewingId: null
		}
	},
	computed: {
		...mapState({
			loading: s => s.gallery.loading,
			fetchedAll: s => s.gallery.done
		}),
		...mapGetters({
			loadedAny: 'gallery/loadedAny',
			results: 'gallery/results'
		}),
		showGallery() {
			return this.loadedAny
		},
		viewing() {
			return this.results.find(r => r.id === this.viewingId)
		}
	},
	methods: {
		...mapActions({
			fetchGallery: 'gallery/fetch'
		}),
		async getGallery(force) {
			if (this.loading) return

			const { success, error } = await this.fetchGallery({ force })
			if (!success) {
				// On fail, push back to homepage and show toast
				if (error) {
					switch (error.status) {
						case 403:
							this.toastWarn(error.message)
							break
						default:
							this.toastErr(error.message)
							break
					}
				}
				this.$router.push({ name: 'Home' })
			}
		},
		onView(id) {
			this.viewingId = id
		},
		dismiss() {
			this.viewingId = null
		}
	},
	created() {
		// Force-fetch gallery
		this.getGallery(true)
	},
	mixins: [toast],
	components: {
		Loading,
		StateButtons,
		SpinnerButton,
		Gallery,
		ButtonGroup,
		PolaroidView
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';
@import '~scss/components';
@import '~scss/mq';
@import '~scss/transitions';

@include fade($spd-wall);

.gallery-view {
	max-width: 1200px;
	margin: 0 auto;
	margin-bottom: 2rem;
	padding: 0 1rem;

	&__loading {
		position: relative;
		height: $loading-h;
	}

	&__empty {
		color: $white;
		display: flex;
		text-align: center;
		align-items: center;
		justify-content: center;
	}

	&__submission {
		@extend %abs-full;
		position: fixed;
		background-color: rgba($black, 0.75);
	}

	@include mq(lg) {
		&__buttons {
			margin-top: 4rem;
			margin-bottom: 4rem;
		}
	}
}

.btn {
	@include button($blue-48, $white, $blue-48, $blue-98);
}
</style>
